// Centralized, runtime SEO for the FileAgent SPA. Applied on every route change:
// title, meta description, canonical URL, Open Graph + Twitter tags, and JSON-LD
// structured data (FAQ schema, software/organization). One source of truth so
// every page — core, landing, and comparison — gets unique, consistent metadata.
import { absoluteSiteUrl, siteConfig } from "./config";
import { planAnchor, plans, type BillingInterval } from "./plans";

export interface FaqItem {
  q: string;
  a: string;
}

export interface SeoMeta {
  title: string;
  description: string;
  /** Path for canonical URL + og:url, e.g. "/pricing". */
  path: string;
  /** Social image (absolute URL or site-relative path). Defaults to the hero. */
  image?: string;
  /** JSON-LD structured data object(s). */
  jsonLd?: object | object[];
}

const DEFAULT_IMAGE = "/fileagent-hero-v2.png?v=20260806";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data?: object | object[]) {
  const existing = document.getElementById("seo-jsonld");
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "seo-jsonld";
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

export function applySeo(meta: SeoMeta): void {
  const url = absoluteSiteUrl(meta.path);
  const rawImage = meta.image || DEFAULT_IMAGE;
  const image = rawImage.startsWith("http") ? rawImage : absoluteSiteUrl(rawImage);

  document.title = meta.title;
  upsertMeta("name", "description", meta.description);
  upsertCanonical(url);

  upsertMeta("property", "og:title", meta.title);
  upsertMeta("property", "og:description", meta.description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", image);

  upsertMeta("name", "twitter:title", meta.title);
  upsertMeta("name", "twitter:description", meta.description);
  upsertMeta("name", "twitter:image", image);

  setJsonLd(meta.jsonLd);
}

/** Google-eligible FAQ rich-result schema. */
export function faqSchema(faq: FaqItem[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/**
 * Published list prices from the plan catalog. Fixed-price plans only: custom
 * (sales-quoted) plans have no public price and are not listed as offers.
 */
function planOffers(): object[] {
  const offers: object[] = [];
  for (const plan of plans) {
    const url = absoluteSiteUrl(`/pricing#${planAnchor(plan)}`);
    const intervals: BillingInterval[] = ["monthly", "annual"];
    const fixed = intervals.filter((interval) => plan.prices[interval].kind === "fixed");
    if (fixed.length !== intervals.length) continue;
    const monthlyMinor = plan.prices.monthly.amountMinor ?? 0;
    const annualMinor = plan.prices.annual.amountMinor ?? 0;
    if (monthlyMinor === 0 && annualMinor === 0) {
      offers.push({ "@type": "Offer", name: `FileAgent ${plan.name}`, price: "0", priceCurrency: "USD", url });
      continue;
    }
    for (const interval of intervals) {
      const price = ((plan.prices[interval].amountMinor ?? 0) / 100).toFixed(2);
      offers.push({
        "@type": "Offer",
        name: `FileAgent ${plan.name} (${interval} subscription)`,
        price,
        priceCurrency: "USD",
        url,
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price,
          priceCurrency: "USD",
          billingDuration: 1,
          unitCode: interval === "annual" ? "ANN" : "MON",
        },
      });
    }
  }
  return offers;
}

/** SoftwareApplication + Organization knowledge-graph schema for the homepage. */
export function appSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "FileAgent",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Windows, Linux",
    description:
      "Self-hosted file transfer automation platform for businesses: schedule, monitor, and audit transfers across local storage, SMB, SFTP, FTP, AWS S3, and Azure Blob from a clean web UI.",
    url: siteConfig.siteUrl,
    offers: planOffers(),
    publisher: {
      "@type": "Organization",
      name: "FileAgent",
      url: siteConfig.siteUrl,
    },
  };
}
